import React, { useEffect } from 'react';
import PageHeader from '../../PageHeader/PageHeader';
import { HitApi } from '../../Api/ApiHit';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../redux/Actions/ApiAction';
import { setPagination } from '../../redux/Actions/TableAction';
import AppTable from '../../components/AppTable/AppTable';
import AppPagination from '../../components/Pagination/AppPagination';
import DeleteButton from '../../components/Delete/DeleteButton';
import EditButton from '../../components/Edit/EditButton';
import { searchMembership, deleteMembership, MembershipBulkUpload } from '../../constant/Constant';
import toast from 'react-hot-toast';
import { getSelectedUnit } from '../../storage/Storage';

function Membership() {
  const dispatch = useDispatch();
  const ApiReducer = useSelector((state) => state.ApiReducer);
  const TableDataReducer = useSelector((state) => state.TableReducer);

  useEffect(() => {
    loadMemberships();
  }, [TableDataReducer.pagination.page, TableDataReducer.pagination.limit]);

  const loadMemberships = () => {
    const json = {
      page: TableDataReducer.pagination.page,
      limit: TableDataReducer.pagination.limit,
      search: {
        unitIds: getSelectedUnit()?._id
      }
    };

    HitApi(json, searchMembership).then((res) => {
      console.log("Membership response:", res);
      if (res?.statusCode === 200) {
        const membershipData = res?.data?.docs || [];

        dispatch(setApiJson(membershipData));
        dispatch(setPagination({
          total: res?.data?.totalDocs || 0,
          page: res?.data?.page || TableDataReducer.pagination.page,
          limit: res?.data?.limit || TableDataReducer.pagination.limit
        }));
      }
    }).catch(error => {
      console.error("Error loading memberships:", error);
      dispatch(setApiJson([]));
    });
  };

  const handleEdit = (row) => {
    console.log("Edit membership:", row);
    window.location.href = `/membership/edit/${row._id}`;
  };

  const handleDelete = (row) => {
    if (window.confirm(`Are you sure you want to delete ${row.name}?`)) {
      HitApi({ _id: row._id }, deleteMembership).then((res) => {
        if (res?.statusCode === 200) {
          toast.success('Membership Deleted successfully');
          loadMemberships();
        }
      }).catch(error => {
        console.error("Error deleting membership:", error);
        toast.error('Failed to delete membership');
      });
    }
  };

  const handleDataRefresh = () => {
    console.log("Refreshing memberships data after bulk upload...");
    loadMemberships();
  };

  const baseIndex = (TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit;

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0.00';
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const formatDuration = (duration) => {
    if (!duration || !duration.value || !duration.unit) return 'N/A';
    return `${duration.value} ${duration.unit}`;
  };

  const tableHeaders = [
    {
      title: "S.No",
      key: "serialNumber",
      width: "60px",
      align: "center",
      priority: 1,
      render: (_, row, index) => (
        <div className="text-center">
          <span className="text-sm font-medium text-gray-900">
            {baseIndex + index + 1}
          </span>
        </div>
      )
    },
    {
      title: "Name",
      key: "name",
      priority: 1,
      render: (value) => (
        <div className="font-medium text-gray-900">
          {value || 'N/A'}
        </div>
      )
    },
    {
      title: "Description",
      key: "description",
      priority: 3,
      render: (value) => (
        <div className="text-sm text-gray-600 max-w-xs truncate" title={value}>
          {value || 'N/A'}
        </div>
      )
    },
    {
      title: "Type",
      key: "membershipType",
      align: "start",
      priority: 2,
      render: (value) => (
        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${value === 'value_added' ? 'bg-blue-100 text-blue-800' :
          value === 'fix_discount' ? 'bg-green-100 text-green-800' :
            'bg-gray-100 text-gray-800'
          }`}>
          {value === 'value_added' ? 'Value Added' :
            value === 'fix_discount' ? 'Fixed Discount' :
              value || 'N/A'}
        </span>
      )
    },
    {
      title: "Price",
      key: "price",
      align: "start",
      priority: 2,
      render: (value, row) => (
        <div className="flex flex-col">
          <span className="font-medium text-green-600">
            {formatCurrency(value)}
          </span>
          {row.valueAddedAmount && (
            <span className="text-xs text-blue-400">
              Value: {formatCurrency(row.valueAddedAmount)}
            </span>
          )}
        </div>
      )
    },
    {
      title: "Benefit",
      key: "benefit",
      align: "start",
      priority: 3,
      render: (value, row) => {
        if (row.membershipType === 'value_added' && row.valueAddedAmount) {
          const savings = row.valueAddedAmount - row.price;
          return (
            <span className="text-blue-600 font-medium">
              Extra {formatCurrency(savings)}
            </span>
          );
        } else if (row.membershipType === 'fix_discount' && row.fixDiscountPercentage) {
          return (
            <span className="text-green-600 font-medium">
              {row.fixDiscountPercentage}% off
            </span>
          );
        }
        return <span className="text-gray-400">N/A</span>;
      }
    },
    {
      title: "Duration",
      key: "duration",
      priority: 3,
      render: (value) => (
        <span className="text-sm text-gray-600">
          {formatDuration(value)}
        </span>
      )
    },
    {
      title: "Status",
      key: "isActive",
      align: "start",
      priority: 2,
      render: (value) => (
        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      title: "Actions",
      key: "actions",
      align: "start",
      width: "120px",
      priority: 1,
      render: (value, row) => (
        <div className="flex justify-start space-x-2">
          <EditButton
            onEdit={() => handleEdit(row)}
            itemName="membership"
          />
          <DeleteButton
            onDelete={() => handleDelete(row)}
            itemName="membership"
          />
        </div>
      )
    }
  ];

  const tableData = Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : [];
  const isLoading = ApiReducer.apiJson === null || ApiReducer.apiJson === undefined;

  return (
    <div className="p-5">
      <PageHeader
        title="Membership Management"
        description="Manage customer memberships, plans, and subscription services"
      />
      <AppTable
        TH={tableHeaders}
        TD={tableData}
        isLoading={isLoading}
        emptyMessage="No memberships found"
        buttonText="Add Membership"
        navigateTo="/membership/add"
      />
    </div>
  );
}

export default Membership;